/**
 * 媒體庫的批次操作列。
 *
 * 144 筆素材下逐列點「編輯」不可能作業，尤其標籤打錯時要一個個改。
 *
 * 沿用既有的 PATCH / DELETE 端點循序處理，不新增批次 API：批次端點要嘛整批
 * 交易（一筆失敗全退，對「補標籤」這種操作太嚴格），要嘛回部分成功的結果集
 * （呼叫端仍得逐筆處理）。循序呼叫在這個量級足夠，且失敗時能明確指出是哪一筆。
 */

import { useState } from 'react';

import { Button } from './Button';
import { deleteMedia, updateMedia, type MediaItem } from '../lib/api-client';

interface MediaBatchBarProps {
  selected: MediaItem[];
  languages: ReadonlyArray<{ value: string; label: string }>;
  onDone: () => void;
  onClear: () => void;
}

type Progress = { done: number; total: number; failed: string[] } | null;

export function MediaBatchBar({
  selected,
  languages,
  onDone,
  onClear,
}: MediaBatchBarProps): JSX.Element {
  const [tagInput, setTagInput] = useState('');
  const [language, setLanguage] = useState('');
  const [progress, setProgress] = useState<Progress>(null);

  const busy = progress !== null && progress.done < progress.total;

  /** 循序執行並累計失敗；單筆失敗不中斷其餘項目。 */
  const run = async (fn: (m: MediaItem) => Promise<unknown>): Promise<void> => {
    const failed: string[] = [];
    setProgress({ done: 0, total: selected.length, failed });
    for (let i = 0; i < selected.length; i += 1) {
      const m = selected[i]!;
      try {
        await fn(m);
      } catch (e) {
        failed.push(`${m.filename}：${(e as Error).message}`);
      }
      setProgress({ done: i + 1, total: selected.length, failed: [...failed] });
    }
    onDone();
  };

  const addTags = (): void => {
    const add = tagInput
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    if (add.length === 0) return;
    void run((m) =>
      // 聯集而非覆寫——批次加標籤不該把既有的資料夾標籤洗掉
      updateMedia(m.id, { tags: [...new Set([...m.tags, ...add])] }),
    ).then(() => setTagInput(''));
  };

  const applyLanguage = (): void => {
    void run((m) => updateMedia(m.id, { language }));
  };

  const remove = (): void => {
    if (!window.confirm(`確定刪除選取的 ${selected.length} 筆素材？`)) return;
    void run((m) => deleteMedia(m.id));
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '8px 12px',
        margin: '8px 0',
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 6,
        fontSize: 13,
      }}
    >
      <strong>已選 {selected.length} 筆</strong>

      <input
        type="text"
        value={tagInput}
        onChange={(e) => setTagInput(e.target.value)}
        placeholder="要加上的標籤（逗號分隔）"
        style={{ padding: 5, width: 210 }}
        disabled={busy}
      />
      <Button variant="secondary" onClick={addTags} disabled={busy || tagInput.trim() === ''}>
        加標籤
      </Button>

      <select
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
        style={{ padding: 5 }}
        disabled={busy}
      >
        {languages.map((l) => (
          <option key={l.value} value={l.value}>
            {l.value === '' ? '（清除語系）' : l.label}
          </option>
        ))}
      </select>
      <Button variant="secondary" onClick={applyLanguage} disabled={busy}>
        套用語系
      </Button>

      <Button variant="danger" onClick={remove} disabled={busy}>
        刪除
      </Button>
      <Button variant="ghost" onClick={onClear} disabled={busy}>
        取消選取
      </Button>

      {progress && (
        <span style={{ color: busy ? '#1d4ed8' : '#065f46' }}>
          {busy ? `處理中 ${progress.done}/${progress.total}` : `完成 ${progress.total} 筆`}
          {progress.failed.length > 0 && `，失敗 ${progress.failed.length} 筆`}
        </span>
      )}
      {progress && progress.failed.length > 0 && !busy && (
        <div className="error-banner" style={{ flexBasis: '100%', fontSize: 12 }}>
          {progress.failed.join('；')}
        </div>
      )}
    </div>
  );
}
